/**
 * 다크모드.
 *
 * 이 사이트는 색이 **인라인 스타일로 650곳 넘게 박혀** 있고 CSS 변수는 쓰지 않는다.
 * 그래서 CSS 로는 손이 닿지 않는다. 세 가지를 재 보고 이 방식으로 정했다.
 *
 * 1. `filter: invert()` 를 상위 요소에 걸기 → **모달이 깨진다.** 필터를 걸면 그 안의
 *    고정 요소가 화면이 아니라 그 상자를 기준으로 잡힌다(실측: 500px 스크롤에 -500px 로
 *    흘렀다). 스크롤한 상태에서 팝업이 엉뚱한 자리에 뜬다.
 * 2. 흰 판을 겹쳐 `difference` 로 뒤집기 → 레이아웃은 멀쩡하지만 **색조가 뒤집힌다.**
 *    승(파랑 #2196F3)이 주황이 되고 패(빨강 #f44336)가 청록이 됐다. 승패 색이 서로
 *    바뀌어 보이니 쓸 수 없다.
 * 3. **밝기만 뒤집기(이 파일).** 인라인 색을 읽어 HSL 의 밝기만 반대로 돌린다. 파랑은
 *    파랑으로, 빨강은 빨강으로 남고 흰 바탕만 어두워진다. 레이아웃도 그대로다.
 *
 * React 가 다시 그릴 때 인라인 스타일을 되돌리므로 MutationObserver 로 다시 칠한다.
 *
 * 고른 값은 이 브라우저에 남는다(localStorage). 사람마다 기기마다 다르게 쓰는 설정이라
 * 계정에 묶지 않는다.
 */
const KEY = 'swpvpdb-theme';
/** 칠한 결과를 적어 두는 자리(data-dark-painted). 같은 값이면 다시 칠하지 않는다 */
const MARK = 'darkPainted';

export type Theme = 'light' | 'dark';

/** 색이 담기는 인라인 속성들 */
const COLOR_PROPS = [
    'color',
    'backgroundColor',
    'background',
    'backgroundImage',
    'borderColor',
    'borderTopColor',
    'borderRightColor',
    'borderBottomColor',
    'borderLeftColor',
    'boxShadow',
    'outlineColor'
] as const;

/** `#abc` `#aabbcc` `rgb(...)` `rgba(...)` 를 모두 잡는다 */
const COLOR_RE = /#[0-9a-fA-F]{3,8}\b|rgba?\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*(?:,\s*[\d.]+\s*)?\)/g;

function parse(color: string): { r: number; g: number; b: number; a: number } | null {
    const hex = color.match(/^#([0-9a-fA-F]{3,8})$/);
    if (hex) {
        let h = hex[1];
        if (h.length === 3) h = h.split('').map((c) => c + c).join('');
        if (h.length !== 6 && h.length !== 8) return null;
        return {
            r: parseInt(h.slice(0, 2), 16),
            g: parseInt(h.slice(2, 4), 16),
            b: parseInt(h.slice(4, 6), 16),
            a: h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1
        };
    }
    const m = color.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)/);
    if (!m) return null;
    return { r: +m[1], g: +m[2], b: +m[3], a: m[4] === undefined ? 1 : +m[4] };
}

/** rgb/rgba 문자열로 되돌린다 */
function toCss(r: number, g: number, b: number, a: number): string {
    return a === 1 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${a})`;
}

/**
 * HSL 의 밝기를 갈아끼워 되돌린다. 색조는 유지한다.
 *
 * `sCap` 을 주면 선명도를 그만큼으로 누른다 — 밝은 파스텔 바탕(#f7faff 처럼 흰색에 색이
 * 살짝 섞인 것)을 그냥 어둡게만 하면 진한 남색이 된다. 그건 원래 "거의 흰색" 이었으니
 * 어두워질 때도 "거의 중성" 이어야 한다.
 */
function withLightness(
    c: { r: number; g: number; b: number; a: number },
    l2: number,
    sCap = 1
): string {
    const r = c.r / 255, g = c.g / 255, b = c.b / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;

    if (d < 0.02) {
        const v = Math.round(l2 * 255);
        return toCss(v, v, v, c.a);
    }
    const s = d / (1 - Math.abs(2 * l - 1));
    let h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h = (h * 60 + 360) % 360;

    const chroma = (1 - Math.abs(2 * l2 - 1)) * Math.min(s, sCap);
    const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l2 - chroma / 2;
    const seg = Math.floor(h / 60) % 6;
    const rgb = [
        [chroma, x, 0], [x, chroma, 0], [0, chroma, x], [0, x, chroma], [x, 0, chroma], [chroma, 0, x]
    ][seg].map((v) => Math.round((v + m) * 255));
    return toCss(rgb[0], rgb[1], rgb[2], c.a);
}

/**
 * 다크모드에서 이 색을 어떻게 할지.
 *
 * **모든 색을 뒤집으면 안 된다.** 밝기를 통째로 뒤집으니 흰 글자가 검게 변해서, 보라
 * 버튼(색은 그대로 두고 싶은 것) 위의 글자가 안 보였다. 그래서 셋으로 나눈다.
 *
 *   글자   — 어두운 글자만 밝게. 흰 글자는 그대로 둔다(색 있는 버튼 위에 얹혀 있다)
 *   바탕   — 밝은 것만 어둡게. 강조색(파랑·빨강·보라)은 그대로 둬야 승패 색이 안 흐트러진다
 *   나머지 — 손대지 않는다
 */
function adjust(color: string, isText: boolean): string {
    const c = parse(color);
    if (!c) return color;

    const r = c.r / 255, g = c.g / 255, b = c.b / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;
    const s = d < 0.02 ? 0 : d / (1 - Math.abs(2 * l - 1));

    if (isText) {
        /**
         * 색 있는 글자(승 파랑·패 빨강·보라 강조)는 **색조는 두고 밝기만 0.78 위로** 올린다(파랑은 0.72 로도 2.6 이었다).
         * 중간 밝기(#667eea, #4c56b5, #d32f2f)를 그대로 두니 남색 바탕에서 대비가 1.7~2.6 이라
         * 닉네임이 안 보였다(사용자 2026-10-22 다크모드 전수조사). 흰 글자는 그대로 둔다.
         */
        if (s > 0.15) return l < 0.78 ? withLightness(c, Math.max(0.78, Math.min(0.85, 1 - l))) : color;
        // 무채색 어두운 글자 → 밝게
        return l < 0.5 ? withLightness(c, Math.min(0.92, 1 - l)) : color;
    }

    /**
     * 밝은 바탕·테두리 → 어둡게. **선명도는 눌러 중성에 가깝게** 만든다.
     * 누르지 않으면 흰색에 색이 살짝 섞인 바탕(#f7faff)이 진한 남색이 되어 튄다.
     * 테두리처럼 살짝 밝은 색은 형태가 남게 조금 밝게 둔다.
     */
    // 무채색 대신 **맨 위 탭 줄과 같은 남색**으로 통일한다(사용자 2026-10-22 "여기저기 쓰이는 이런 스타일도 색을 통일") —
    // 흰 판은 탭 줄 아래 색 #2c3248, 살짝 밝은 바탕·테두리는 한 단계 밝은 #363d57. 투명도는 그대로
    if (l > 0.72) return l > 0.9 ? toCss(44, 50, 72, c.a) : toCss(54, 61, 87, c.a);

    // 중간 밝기의 선명한 색(강조색)과 이미 어두운 색은 그대로
    return color;
}

/**
 * 한 요소의 인라인 색을 뒤집는다.
 *
 * **다시 칠하지 않도록** 칠한 결과를 그대로 적어 둔다. 우리가 쓴 값이 그대로면 건너뛴다.
 * 이게 없으면 우리가 style 을 쓰는 것을 감시자가 다시 잡아 끝없이 덧칠한다(화면이 멈췄다).
 */
function paint(el: HTMLElement): void {
    // 화면이 다크 색을 직접 고른 자리(공지 목록·본문 등) — 덧칠하지 않는다
    if (el.closest('[data-dark-skip]')) return;
    if (overBudget()) return;
    const before = el.getAttribute('style') || '';
    if (!before) return;
    if (el.dataset[MARK] === before) return;

    for (const prop of COLOR_PROPS) {
        const raw = el.style[prop as any] as string;
        if (!raw) continue;
        COLOR_RE.lastIndex = 0;
        if (!COLOR_RE.test(raw)) continue;
        COLOR_RE.lastIndex = 0;
        const isText = prop === 'color';
        const next = raw.replace(COLOR_RE, (m) => adjust(m, isText));
        if (next !== raw) el.style[prop as any] = next;
    }
    el.dataset[MARK] = el.getAttribute('style') || '';
}

let observer: MutationObserver | null = null;

/**
 * 폭주 방지.
 *
 * 다시 칠하는 것이 또 감지되어 끝없이 도는 사고가 한 번 있었다(화면이 멈췄다). 지금은
 * 칠한 결과를 적어 둬서 수렴하지만, 만약을 위해 1초에 이만큼을 넘으면 손을 뗀다 —
 * 다크모드가 안 먹는 것이 화면이 멈추는 것보다 낫다.
 */
const PAINT_BUDGET = 20000;
let paintCount = 0;
let budgetWindowAt = 0;

function overBudget(): boolean {
    const now = Date.now();
    if (now - budgetWindowAt > 1000) {
        budgetWindowAt = now;
        paintCount = 0;
    }
    paintCount += 1;
    if (paintCount <= PAINT_BUDGET) return false;
    console.warn('[다크모드] 다시 칠하기가 너무 잦아 멈춥니다. 화면은 밝은 색으로 남습니다.');
    stopPainting();
    return true;
}

function paintAll(): void {
    const root = document.getElementById('root');
    if (!root) return;
    paint(root);
    root.querySelectorAll<HTMLElement>('[style]').forEach(paint);
}

function startPainting(): void {
    paintAll();
    if (observer) return;
    // React 가 다시 그리면 인라인 색이 되살아난다 — 새로 생긴 것만 다시 칠한다
    observer = new MutationObserver((records) => {
        for (const r of records) {
            // 우리가 쓴 값이면 paint 가 알아서 건너뛴다(위 주석 참고)
            if (r.type === 'attributes' && r.target instanceof HTMLElement) paint(r.target);
            r.addedNodes.forEach((n) => {
                if (!(n instanceof HTMLElement)) return;
                paint(n);
                n.querySelectorAll<HTMLElement>('[style]').forEach(paint);
            });
        }
    });
    observer.observe(document.getElementById('root') as Node, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['style']
    });
}

function stopPainting(): void {
    observer?.disconnect();
    observer = null;
}

export function getTheme(): Theme {
    try {
        return localStorage.getItem(KEY) === 'dark' ? 'dark' : 'light';
    } catch {
        return 'light';
    }
}

export function applyTheme(theme: Theme): void {
    const root = document.documentElement;
    if (theme === 'dark') {
        root.setAttribute('data-theme', 'dark');
        startPainting();
    } else {
        root.removeAttribute('data-theme');
        stopPainting();
    }
}

export function setTheme(theme: Theme): void {
    try {
        localStorage.setItem(KEY, theme);
    } catch {
        // 저장을 못 해도 이번 화면에는 적용한다
    }
    if (theme === 'light') {
        // 칠해 둔 인라인 색을 원래대로 되돌리는 가장 확실한 방법은 다시 그리는 것이다
        applyTheme('light');
        window.location.reload();
        return;
    }
    applyTheme(theme);
}

/** 앱이 뜰 때 한 번. 화면이 생기기 전이라 그리기는 첫 렌더 뒤에 붙는다 */
export function initTheme(): void {
    const theme = getTheme();
    const root = document.documentElement;
    if (theme === 'dark') root.setAttribute('data-theme', 'dark');
    if (theme !== 'dark') return;
    // 첫 렌더가 끝난 뒤부터 칠한다
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => startPainting());
    } else {
        startPainting();
    }
}
